import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCalculator } from '@/hooks/useCalculator';
import { useSession } from '@/context/SessionContext';
import { AVAILABLE_CHEMICALS } from '@/types/calculator';
import type { DosingConfig } from '@/types/calculator';
import {
  FlaskConical, DollarSign, Activity, Save, RotateCcw,
  AlertTriangle, CheckCircle2, Beaker,
  ChevronDown, ChevronUp, Package, Truck, BarChart3, Zap,
  TrendingUp, Calendar, Droplets, Lock, Unlock, Info,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';

// ─── Chemical pricing per kg USD ───
const CHEMICAL_PRICING: Record<string, { costPerKg: number; unitSize: string; source: string; deliveryDays: number }> = {
  fecl3: { costPerKg: 2.80, unitSize: '25 kg IBC', source: 'Industrial grade', deliveryDays: 3 },
  alum: { costPerKg: 1.20, unitSize: '25 kg bag', source: 'Commercial grade', deliveryDays: 2 },
  pac: { costPerKg: 3.50, unitSize: '200L drum', source: 'Liquid PAC 10%', deliveryDays: 3 },
  polymer: { costPerKg: 12.00, unitSize: '25 kg bag', source: 'Anionic PAM', deliveryDays: 5 },
  lime: { costPerKg: 0.60, unitSize: 'Bulk tanker', source: 'Hydrated Ca(OH)₂', deliveryDays: 7 },
  caustic: { costPerKg: 1.80, unitSize: '200L drum', source: 'NaOH 32%', deliveryDays: 3 },
  feso4: { costPerKg: 1.50, unitSize: '25 kg bag', source: 'Ferrous sulfate', deliveryDays: 4 },
  chlorine: { costPerKg: 2.20, unitSize: '200L drum', source: 'NaOCl 12-15%', deliveryDays: 2 },
};

// ─── Color map ───
const CHEM_COLORS: Record<string, string> = {
  fecl3: '#f59e0b', alum: '#3b82f6', pac: '#06b6d4', feso4: '#22c55e',
  lime: 'var(--rb-text-2)', caustic: '#ef4444', polymer: '#a855f7', chlorine: '#f97316',
};
function chemColor(id: string) { return CHEM_COLORS[id] || 'var(--rb-text-3)'; }

// ─── Info for expanded panel ───
const CHEM_INFO: Record<string, { bestFor: string; alternatives: string[]; notes: string }> = {
  fecl3: { bestFor: 'High TSS, phosphorus removal', alternatives: ['alum', 'pac'], notes: 'Ferric chloride is the most common coagulant for wastewater. Effective over a wide pH range (5-9). Corrosive — use stainless steel or PVC dosing lines.' },
  alum: { bestFor: 'General purpose, low cost', alternatives: ['fecl3', 'pac'], notes: 'Aluminium sulfate is economical but less effective at low temperatures. Best pH range 6.5-7.5. Produces more sludge than PAC.' },
  pac: { bestFor: 'Cold water, low turbidity', alternatives: ['fecl3', 'alum'], notes: 'Poly-aluminium chloride works well at low temperatures and produces denser sludge. Pre-hydrolysed form reacts faster than alum.' },
  polymer: { bestFor: 'Sludge dewatering, flocculation', alternatives: [], notes: 'Anionic PAM is the workhorse for sludge dewatering. Cationic forms used for oily wastewater. Always add after coagulant.' },
  lime: { bestFor: 'pH adjustment, phosphorus removal', alternatives: ['caustic'], notes: 'Hydrated lime raises pH and precipitates phosphorus. Very low cost but requires slaking equipment. Best for flows >500 m³/d.' },
  caustic: { bestFor: 'Rapid pH adjustment', alternatives: ['lime'], notes: 'Sodium hydroxide (32% solution) gives precise pH control. Safer handling than quicklime but higher cost per kg alkalinity.' },
  feso4: { bestFor: 'Chrome reduction, odour control', alternatives: ['fecl3'], notes: 'Ferrous sulfate is cheaper than ferric chloride and adds less chloride to effluent. Requires oxidation to ferric form for P-removal.' },
  chlorine: { bestFor: 'Disinfection, odour control', alternatives: [], notes: 'Sodium hypochlorite (12-15%) for final effluent disinfection. Dechlorination may be required before discharge. UV is a chemical-free alternative.' },
};

// ─── localStorage helpers ───
const DOSING_STORAGE_KEY = 'rb_dosing_config_v2';
const DOSING_LOCK_KEY = 'rb_dosing_lock_state';

function loadStoredDosing(): DosingConfig[] | null {
  try { const raw = localStorage.getItem(DOSING_STORAGE_KEY); if (raw) return JSON.parse(raw); } catch { /* ignore */ }
  return null;
}
function saveStoredDosing(configs: DosingConfig[]) {
  try { localStorage.setItem(DOSING_STORAGE_KEY, JSON.stringify(configs)); } catch { /* ignore */ }
}
function loadLockState(): Record<string, boolean> | null {
  try { const raw = localStorage.getItem(DOSING_LOCK_KEY); if (raw) return JSON.parse(raw); } catch { /* ignore */ }
  return null;
}
function saveLockState(state: Record<string, boolean>) {
  try { localStorage.setItem(DOSING_LOCK_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

// ═══════════════════════════════════════════════════════════════════
// STANDALONE ChemicalCard — defined OUTSIDE DosingPage to prevent
// full remount on every slider state change
// ═══════════════════════════════════════════════════════════════════
interface ChemicalCardProps {
  chem: typeof AVAILABLE_CHEMICALS[0];
  index: number;
  ctrl: { enabled: boolean; dose: number; originalDose: number };
  isRec: boolean;
  isLocked: boolean;
  expandedChem: string | null;
  onToggleEnabled: (id: string) => void;
  onToggleLock: (id: string) => void;
  onUpdateDose: (id: string, val: number) => void;
  onExpand: (id: string | null) => void;
}

function ChemicalCard({
  chem, index, ctrl, isRec, isLocked, expandedChem,
  onToggleEnabled, onToggleLock, onUpdateDose, onExpand,
}: ChemicalCardProps) {
  const isOpen = expandedChem === chem.id;
  const isModified = ctrl.dose !== ctrl.originalDose;
  const maxDose = chem.typicalDoseMax;
  const pct = Math.min(100, (ctrl.dose / maxDose) * 100);
  const phChange = chem.phChangePerMgL * ctrl.dose;
  const color = chemColor(chem.id);
  const info = CHEM_INFO[chem.id];
  const pricing = CHEMICAL_PRICING[chem.id];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, layout: { duration: 0.2 } }}
      className={`rb-card p-4 transition-all ${
        ctrl.enabled
          ? isRec ? 'border-rb-brand/40 shadow-lg shadow-[#cda42e]/5' : 'border-cyan-500/30 shadow-lg shadow-cyan-500/5'
          : 'border-slate-800/50'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
            <FlaskConical className="w-4 h-4" style={{ color }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-white">{chem.name}</h3>
              {isRec && (
                <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-rb-brand/15 text-rb-brand border border-rb-brand/30 font-bold">IN TRAIN</span>
              )}
              {isModified && (
                <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-rb-surface/10 text-slate-300 border border-white/20 font-bold">MOD</span>
              )}
            </div>
            <span className="text-[10px] text-slate-500 font-mono">{chem.formula} · {chem.doseUnit}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <button onClick={() => onToggleLock(chem.id)}
            className={`p-1.5 rounded-lg transition-all ${
              isLocked ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20'
            }`} title={isLocked ? 'Unlock to adjust dose' : 'Lock dose'}>
            {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
          </button>
          <button onClick={() => onToggleEnabled(chem.id)}
            className={`relative w-10 h-5 rounded-full transition-all ${ctrl.enabled ? 'bg-cyan-500/20' : 'bg-slate-700'}`}>
            <div className={`absolute top-[2px] left-[2px] w-4 h-4 rounded-full transition-all ${ctrl.enabled ? 'bg-cyan-400 translate-x-5' : 'bg-slate-500'}`} />
          </button>
        </div>
      </div>

      {/* ─── ALWAYS VISIBLE COLORED SLIDER ─── */}
      <div className={`space-y-2 ${ctrl.enabled ? 'opacity-100' : 'opacity-50 grayscale-[0.3]'}`}>

        {/* Dose info line: average or recommended, always visible */}
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-slate-500">
            {isRec ? (
              <span className="text-rb-brand font-bold">Recommended: {ctrl.dose} {chem.doseUnit}</span>
            ) : (
              <span className="text-slate-400">Average: {Math.round((chem.typicalDoseMin + chem.typicalDoseMax) / 2)} {chem.doseUnit}</span>
            )}
          </span>
          <span className="text-slate-600">Range: {chem.typicalDoseMin}-{maxDose} {chem.doseUnit}</span>
        </div>

        {isLocked && ctrl.enabled && (
          <div className="flex items-center gap-1.5 text-[10px] text-amber-400/80 bg-amber-500/5 border border-amber-500/10 rounded-lg px-2 py-1">
            <Lock className="w-3 h-3 shrink-0" />
            <span>Locked — click <Unlock className="w-3 h-3 inline" /> to adjust dose</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Dose</span>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold" style={{ color: ctrl.enabled ? color : 'var(--rb-text-3)' }}>{ctrl.dose}</span>
            <span className="text-[10px] text-slate-500">{chem.doseUnit}</span>
          </div>
        </div>

        <div className="relative">
          <input
            type="range"
            min={chem.typicalDoseMin}
            max={maxDose}
            step={chem.id === 'polymer' ? 0.5 : chem.id === 'chlorine' ? 1 : 5}
            value={ctrl.dose}
            onChange={e => onUpdateDose(chem.id, parseFloat(e.target.value))}
            disabled={isLocked || !ctrl.enabled}
            className={`w-full h-2 rounded-lg appearance-none transition-opacity ${
              isLocked || !ctrl.enabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            style={{
              background: `linear-gradient(to right, ${color} ${pct}%, #1e293b ${pct}%)`,
              outline: 'none',
              opacity: isLocked || !ctrl.enabled ? 0.5 : 1,
            }}
          />
          <div className="flex justify-between mt-0.5">
            <span className="text-[10px] text-slate-600">{chem.typicalDoseMin}</span>
            <span className="text-[10px] text-slate-600">{Math.round(maxDose / 2)}</span>
            <span className="text-[10px] text-slate-600">{maxDose}</span>
          </div>
        </div>

        {/* 4-column metrics */}
        <div className="grid grid-cols-4 gap-2 pt-2 border-t border-slate-800">
          <div className="text-center">
            <span className="text-[10px] text-slate-500 block">TSS &#8595;</span>
            <span className="text-xs font-bold text-emerald-400">{chem.tssReductionPercent}%</span>
          </div>
          <div className="text-center">
            <span className="text-[10px] text-slate-500 block">BOD &#8595;</span>
            <span className="text-xs font-bold text-cyan-400">{chem.bodReductionPercent}%</span>
          </div>
          <div className="text-center">
            <span className="text-[10px] text-slate-500 block">COD &#8595;</span>
            <span className="text-xs font-bold text-purple-400">{chem.codReductionPercent}%</span>
          </div>
          <div className="text-center">
            <span className="text-[10px] text-slate-500 block">pH &#916;</span>
            <span className={`text-xs font-bold ${phChange > 0 ? 'text-red-400' : phChange < 0 ? 'text-blue-400' : 'text-slate-400'}`}>
              {phChange > 0 ? '+' : ''}{phChange.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* More Info toggle */}
      <button onClick={() => onExpand(isOpen ? null : chem.id)}
        className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 transition-colors mx-auto mt-3">
        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {isOpen ? 'Less Info' : 'More Info'}
      </button>

      {/* Expanded detail */}
      <AnimatePresence>
        {isOpen && info && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }} className="overflow-hidden"
          >
            <div className="mt-3 p-3 rounded-lg bg-slate-800/40 border border-slate-800/50 space-y-2 text-xs text-slate-400">
              <p><span className="text-slate-500 font-bold">Best for:</span> {info.bestFor}</p>
              <p><span className="text-slate-500 font-bold">Typical range:</span> {chem.typicalDoseMin}-{chem.typicalDoseMax} {chem.doseUnit}</p>
              <p><span className="text-slate-500 font-bold">Description:</span> {chem.description}</p>
              {info.alternatives.length > 0 && (
                <p><span className="text-slate-500 font-bold">Alternatives:</span> {info.alternatives.map(a => AVAILABLE_CHEMICALS.find(c => c.id === a)?.name).join(', ')}</p>
              )}
              <div className="pt-1 border-t border-slate-800/50">
                <p className="text-[10px] text-slate-500 leading-relaxed"><Info className="w-3 h-3 inline mr-1 text-rb-brand" />{info.notes}</p>
              </div>
              {pricing && (
                <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-500 pt-1">
                  <span><DollarSign className="w-3 h-3 inline text-emerald-400" /> ${pricing.costPerKg.toFixed(2)}/kg</span>
                  <span><Package className="w-3 h-3 inline text-cyan-400" /> {pricing.unitSize}</span>
                  <span><Truck className="w-3 h-3 inline text-amber-400" /> {pricing.deliveryDays}d delivery</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// MAIN DOSING PAGE
// ═══════════════════════════════════════════════════════════════════
export default function DosingPage() {
  const calc = useCalculator();
  const { activeStandard } = useSession();
  const [activeTab, setActiveTab] = useState<'library' | 'process' | 'cost'>('library');
  const [expandedChem, setExpandedChem] = useState<string | null>(null);

  const flowRateM3PerDay = calc.params.flowRate || 500;
  const flowRateLitresPerDay = flowRateM3PerDay * 1000;

  const recommendedIds = useMemo(() => {
    const recs = calc.result?.recommendedDosings ?? [];
    return new Set(recs.map(r => r.chemicalId));
  }, [calc.result]);

  const equipmentReqs = calc.result?.equipmentDosingRequirements ?? [];

  // Lock state — each chemical starts LOCKED by default
  const [locked, setLocked] = useState<Record<string, boolean>>(() => {
    const stored = loadLockState();
    if (stored) return stored;
    const init: Record<string, boolean> = {};
    for (const chem of AVAILABLE_CHEMICALS) init[chem.id] = true;
    return init;
  });

  const toggleLock = useCallback((chemId: string) => {
    setLocked(prev => { const next = { ...prev, [chemId]: !prev[chemId] }; saveLockState(next); return next; });
  }, []);

  // Dosing controls for all 8 chemicals
  const [dosingControls, setDosingControls] = useState<Record<string, { enabled: boolean; dose: number; originalDose: number }>>(() => {
    const stored = loadStoredDosing();
    const base: Record<string, { enabled: boolean; dose: number; originalDose: number }> = {};
    if (stored) {
      for (const s of stored) base[s.chemicalId] = { enabled: s.enabled, dose: s.dose, originalDose: s.dose };
    }
    for (const chem of AVAILABLE_CHEMICALS) {
      if (!base[chem.id]) {
        const calcDefault = calc.dosingConfig.find(d => d.chemicalId === chem.id);
        const recDose = calc.result?.recommendedDosings?.find(r => r.chemicalId === chem.id)?.recommendedDose;
        const defaultDose = calcDefault?.dose ?? recDose ?? chem.typicalDoseMin;
        base[chem.id] = {
          enabled: calcDefault?.enabled ?? false,
          dose: defaultDose,
          originalDose: defaultDose,
        };
      }
    }
    return base;
  });

  // These handlers are stable (useCallback) — ChemicalCard won't remount
  const updateDose = useCallback((chemId: string, newDose: number) => {
    setDosingControls(prev => {
      const next = { ...prev };
      if (next[chemId]) next[chemId] = { ...next[chemId], dose: Math.round(newDose * 10) / 10 };
      return next;
    });
  }, []);

  const toggleEnabled = useCallback((chemId: string) => {
    setDosingControls(prev => {
      const next = { ...prev };
      if (next[chemId]) next[chemId] = { ...next[chemId], enabled: !next[chemId].enabled };
      return next;
    });
  }, []);

  const handleSaveDosing = useCallback(() => {
    const configs: DosingConfig[] = AVAILABLE_CHEMICALS.map(chem => ({
      enabled: dosingControls[chem.id]?.enabled ?? false,
      chemicalId: chem.id,
      dose: dosingControls[chem.id]?.dose ?? chem.typicalDoseMin,
    }));
    saveStoredDosing(configs);
    configs.forEach((cfg, i) => { if (i < calc.dosingConfig.length) calc.updateDosing(i, cfg); });
    toast.success('Dosing configuration saved');
  }, [dosingControls, calc]);

  const handleReset = useCallback(() => {
    const reset: Record<string, { enabled: boolean; dose: number; originalDose: number }> = {};
    for (const chem of AVAILABLE_CHEMICALS) {
      const recDose = calc.result?.recommendedDosings?.find(r => r.chemicalId === chem.id)?.recommendedDose;
      reset[chem.id] = { enabled: false, dose: recDose ?? chem.typicalDoseMin, originalDose: recDose ?? chem.typicalDoseMin };
    }
    setDosingControls(reset);
    const allLocked: Record<string, boolean> = {};
    for (const chem of AVAILABLE_CHEMICALS) allLocked[chem.id] = true;
    setLocked(allLocked); saveLockState(allLocked);
    toast.info('Dosing reset to defaults');
  }, [calc.result]);

  // ─── COST CALCULATIONS ───
  const costRows = useMemo(() =>
    AVAILABLE_CHEMICALS
      .filter(chem => dosingControls[chem.id]?.enabled)
      .map(chem => {
        const ctrl = dosingControls[chem.id];
        const pricing = CHEMICAL_PRICING[chem.id];
        if (!ctrl || !pricing) return null;
        const dailyKg = (ctrl.dose * flowRateLitresPerDay) / 1_000_000;
        const dailyCost = dailyKg * pricing.costPerKg;
        return {
          chem, ctrl, pricing, dailyKg, dailyCost,
          weeklyCost: dailyCost * 7, monthlyCost: dailyCost * 30, annualCost: dailyCost * 365,
          phImpact: chem.phChangePerMgL * ctrl.dose,
          isRecommended: recommendedIds.has(chem.id),
        };
      })
      .filter(Boolean) as NonNullable<ReturnType<typeof costRows>[number]>[]
  , [dosingControls, flowRateLitresPerDay, recommendedIds]);

  const totalDaily = costRows.reduce((s, r) => s + r.dailyCost, 0);
  const totalWeekly = costRows.reduce((s, r) => s + r.weeklyCost, 0);
  const totalMonthly = costRows.reduce((s, r) => s + r.monthlyCost, 0);
  const recommendedCostRows = costRows.filter(r => r.isRecommended);
  const otherCostRows = costRows.filter(r => !r.isRecommended);

  // Tab button helper
  const tabBtn = (key: 'library' | 'process' | 'cost', label: string, icon: React.ElementType, count?: number) => (
    <button key={key} onClick={() => setActiveTab(key)}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold uppercase tracking-wider transition-all border ${
        activeTab === key
          ? 'bg-rb-brand/15 border-rb-brand/40 text-rb-brand shadow-lg shadow-[#cda42e]/10'
          : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
      }`}>
      <icon className="w-4 h-4" />{label}
      {count !== undefined && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-300">{count}</span>}
    </button>
  );

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FlaskConical}
        title="Chemical Dosing Management"
        subtitle="Configure coagulant, polymer and chemical treatment dosing"
        actions={
          <div className="flex items-center gap-2">
            {tabBtn('library', 'Chemical Library', Beaker, AVAILABLE_CHEMICALS.length)}
            {tabBtn('process', 'Process Train', Zap, recommendedIds.size)}
            {tabBtn('cost', 'Cost Analysis', DollarSign, costRows.length)}
          </div>
        }
      />

      {/* Action bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={handleSaveDosing}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all shadow-lg shadow-[#cda42e]/10">
            <Save className="w-3.5 h-3.5" /> Save & Remember
          </button>
          <button onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-400 border border-slate-700 hover:border-slate-500 hover:text-slate-200 transition-all">
            <RotateCcw className="w-3.5 h-3.5" /> Reset to Defaults
          </button>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-amber-400" />{Object.values(locked).filter(Boolean).length} locked</span>
          <span className="flex items-center gap-1"><Unlock className="w-3 h-3 text-emerald-400" />{Object.values(locked).filter(v => !v).length} unlocked</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rb-brand" />{recommendedIds.size} in train</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {/* ═══ TAB 1: CHEMICAL LIBRARY ═══ */}
        {activeTab === 'library' && (
          <motion.div key="library" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {AVAILABLE_CHEMICALS.map((chem, i) => {
              const ctrl = dosingControls[chem.id];
              if (!ctrl) return null;
              return (
                <ChemicalCard
                  key={chem.id}
                  chem={chem}
                  index={i}
                  ctrl={ctrl}
                  isRec={recommendedIds.has(chem.id)}
                  isLocked={locked[chem.id] ?? true}
                  expandedChem={expandedChem}
                  onToggleEnabled={toggleEnabled}
                  onToggleLock={toggleLock}
                  onUpdateDose={updateDose}
                  onExpand={setExpandedChem}
                />
              );
            })}
          </motion.div>
        )}

        {/* ═══ TAB 2: PROCESS TRAIN ═══ */}
        {activeTab === 'process' && (
          <motion.div key="process" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
            <div className={`p-4 rounded-xl border flex items-start gap-3 ${calc.hasCalculated ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
              {calc.hasCalculated ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />}
              <div>
                <p className={`text-sm font-bold ${calc.hasCalculated ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {calc.hasCalculated ? 'Assessment Complete — Process Train Dosing Active' : 'No Assessment Run'}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {calc.hasCalculated
                    ? `${recommendedIds.size} chemicals recommended. Unlock sliders to adjust. All sliders show recommended values.`
                    : 'Go to Parameters → Run Assessment to generate dosing recommendations.'}
                </p>
              </div>
            </div>

            {equipmentReqs.length > 0 && (
              <div className="rb-card p-4">
                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-rb-brand" /> Equipment-Specific Requirements ({equipmentReqs.length})
                </h4>
                <div className="space-y-2">
                  {equipmentReqs.map((req, i) => (
                    <div key={i} className="p-3 rounded-lg bg-slate-800/40 border border-slate-800/50">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-white">{req.equipmentName}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${
                          req.severity === 'required' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                          req.severity === 'recommended' ? 'bg-rb-brand/10 text-rb-brand border-rb-brand/20' :
                          'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
                        }`}>{req.severity}</span>
                      </div>
                      <p className="text-xs text-slate-400">{req.reason}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {req.recommendedChemicals.map(chem => (
                          <span key={chem.chemicalId} className="text-[10px] px-2 py-1 rounded bg-slate-700 text-slate-300 border border-slate-600">
                            {chem.name}: {chem.recommendedDose} {chem.doseUnit}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {calc.result?.recommendedDosings && calc.result.recommendedDosings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {calc.result.recommendedDosings.map((rec, i) => {
                  const chem = AVAILABLE_CHEMICALS.find(c => c.id === rec.chemicalId);
                  if (!chem) return null;
                  const ctrl = dosingControls[chem.id];
                  if (!ctrl) return null;
                  return (
                    <ChemicalCard
                      key={chem.id}
                      chem={chem}
                      index={i}
                      ctrl={ctrl}
                      isRec={true}
                      isLocked={locked[chem.id] ?? true}
                      expandedChem={expandedChem}
                      onToggleEnabled={toggleEnabled}
                      onToggleLock={toggleLock}
                      onUpdateDose={updateDose}
                      onExpand={setExpandedChem}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="rb-card p-12 text-center">
                <Activity className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400 font-medium">No process train assessment available</p>
                <p className="text-xs text-slate-600 mt-1">Run an assessment in the Parameters page to see recommended dosing</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ═══ TAB 3: COST ANALYSIS ═══ */}
        {activeTab === 'cost' && (
          <motion.div key="cost" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Daily', value: `$${totalDaily.toFixed(2)}`, icon: DollarSign, color: '#22d3ee', sub: `${flowRateM3PerDay} m³/day` },
                { label: 'Weekly', value: `$${totalWeekly.toFixed(0)}`, icon: Calendar, color: 'var(--rb-brand)', sub: '7 days' },
                { label: 'Monthly', value: `$${totalMonthly.toFixed(0)}`, icon: TrendingUp, color: '#34d399', sub: '30 days' },
                { label: 'Annual', value: `$${(totalDaily * 365).toFixed(0)}`, icon: BarChart3, color: '#a78bfa', sub: '365 days' },
              ].map((kpi, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="rb-card p-4 text-center hover:shadow-lg hover:shadow-cyan-500/10 transition-all">
                  <kpi.icon className="w-5 h-5 mx-auto mb-2" style={{ color: kpi.color }} />
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{kpi.label}</p>
                  <p className="text-xl font-bold text-white mt-1">{kpi.value}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{kpi.sub}</p>
                </motion.div>
              ))}
            </div>

            {costRows.length === 0 ? (
              <div className="rb-card p-12 text-center">
                <DollarSign className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Enable chemicals to see cost estimates</p>
              </div>
            ) : (
              <>
                {recommendedCostRows.length > 0 && (
                  <div className="rb-card overflow-hidden">
                    <div className="px-5 py-3 border-b border-rb-brand/20 bg-rb-brand/5 flex items-center justify-between">
                      <h4 className="text-[10px] font-bold text-rb-brand uppercase tracking-wider flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5" /> Process Train Chemicals ({recommendedCostRows.length})
                      </h4>
                      <span className="text-[10px] text-rb-brand font-bold">${recommendedCostRows.reduce((s, r) => s + r.monthlyCost, 0).toFixed(0)}/mo</span>
                    </div>
                    <CostTable rows={recommendedCostRows} />
                  </div>
                )}
                {otherCostRows.length > 0 && (
                  <div className="rb-card overflow-hidden">
                    <div className="px-5 py-3 border-b border-cyan-500/20 bg-cyan-500/5 flex items-center justify-between">
                      <h4 className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Beaker className="w-3.5 h-3.5" /> Additional Chemicals ({otherCostRows.length})
                      </h4>
                      <span className="text-[10px] text-cyan-400 font-bold">${otherCostRows.reduce((s, r) => s + r.monthlyCost, 0).toFixed(0)}/mo</span>
                    </div>
                    <CostTable rows={otherCostRows} />
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 rounded-xl border border-rb-brand/20 bg-rb-brand/5">
                  <div className="text-center">
                    <p className="text-[10px] text-slate-500 uppercase">Process Train /mo</p>
                    <p className="text-lg font-bold text-rb-brand">${recommendedCostRows.reduce((s, r) => s + r.monthlyCost, 0).toFixed(0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-slate-500 uppercase">Additional /mo</p>
                    <p className="text-lg font-bold text-cyan-400">${otherCostRows.reduce((s, r) => s + r.monthlyCost, 0).toFixed(0)}</p>
                  </div>
                  <div className="text-center md:col-span-2">
                    <p className="text-[10px] text-slate-500 uppercase">Grand Total Monthly</p>
                    <p className="text-2xl font-bold text-white">${totalMonthly.toFixed(0)}</p>
                  </div>
                </div>
                <div className="p-4 rounded-xl border border-slate-800/50 bg-slate-800/30 flex items-start gap-3">
                  <Truck className="w-5 h-5 text-slate-500 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-slate-300">Delivery & Logistics</p>
                    <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
                      Costs are estimates based on industrial bulk pricing. <span className="text-rb-brand">Polymer</span> and <span className="text-rb-brand">PAC</span> are typically the highest-cost items. For <span className="text-emerald-400">lime</span>, consider bulk tanker delivery for flows &gt;1000 m³/day.
                    </p>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ─── Cost Table ─── */
function CostTable({ rows }: { rows: Array<{ chem: typeof AVAILABLE_CHEMICALS[0]; ctrl: { dose: number; enabled: boolean }; pricing: { costPerKg: number; unitSize: string; source: string }; dailyKg: number; dailyCost: number; weeklyCost: number; monthlyCost: number; annualCost: number; phImpact: number; isRecommended: boolean }> }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-slate-500 uppercase tracking-wider border-b border-slate-800">
            <th className="px-4 py-2.5 font-bold">Chemical</th>
            <th className="px-4 py-2.5 font-bold text-right">Dose</th>
            <th className="px-4 py-2.5 font-bold text-right">$/kg</th>
            <th className="px-4 py-2.5 font-bold text-right">kg/day</th>
            <th className="px-4 py-2.5 font-bold text-right">Daily</th>
            <th className="px-4 py-2.5 font-bold text-right">Weekly</th>
            <th className="px-4 py-2.5 font-bold text-right">Monthly</th>
            <th className="px-4 py-2.5 font-bold text-right">Annual</th>
            <th className="px-4 py-2.5 font-bold text-center">pH &#916;</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/50">
          {rows.map((d, i) => (
            <motion.tr key={d.chem.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }} className="hover:bg-slate-800/20 transition-colors">
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.isRecommended ? 'var(--rb-brand)' : '#06b6d4' }} />
                  <div>
                    <span className="text-sm font-bold text-white">{d.chem.name}</span>
                    <span className="text-[10px] text-slate-500 font-mono ml-1">{d.chem.formula}</span>
                  </div>
                </div>
                <span className="text-[10px] text-slate-600 block mt-0.5">{d.pricing.source} · {d.pricing.unitSize}</span>
              </td>
              <td className="px-4 py-2.5 text-right font-bold text-cyan-400">{d.ctrl.dose} {d.chem.doseUnit}</td>
              <td className="px-4 py-2.5 text-right text-slate-400">${d.pricing.costPerKg.toFixed(2)}</td>
              <td className="px-4 py-2.5 text-right text-slate-300">{d.dailyKg.toFixed(2)}</td>
              <td className="px-4 py-2.5 text-right font-bold text-white">${d.dailyCost.toFixed(2)}</td>
              <td className="px-4 py-2.5 text-right font-bold text-slate-300">${d.weeklyCost.toFixed(0)}</td>
              <td className="px-4 py-2.5 text-right font-bold text-rb-brand">${d.monthlyCost.toFixed(0)}</td>
              <td className="px-4 py-2.5 text-right font-bold text-emerald-400">${d.annualCost.toFixed(0)}</td>
              <td className="px-4 py-2.5 text-center">
                <span className={`text-xs font-bold ${d.phImpact > 0 ? 'text-red-400' : d.phImpact < 0 ? 'text-blue-400' : 'text-slate-400'}`}>
                  {d.phImpact > 0 ? '+' : ''}{d.phImpact.toFixed(1)}
                </span>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
